import { useEffect, useState } from "react";
import { Laptop, PlugZap, Loader2 } from "lucide-react";
import { LIVE, LOST, RETURNING, watchTunnel } from "../lib/tunnel-link";
import { useTaal } from "@/contexts/TaalContext";

/**
 * A line that says this app is running on somebody else's machine.
 *
 * Worth a permanent place on the screen rather than a toast. Everything the
 * user sees is coming over a channel to their Mac, and when that Mac goes to
 * sleep the app stops working in a way that looks exactly like a broken app.
 * A user who can see where their data is coming from can tell the difference.
 */
/**
 * PUNT 2, 12 OKTOBER 2026: DE BALK STAAT IN DE VEILIGE ZONE.
 *
 * Glasronde v0.9.150: "Draait op je Mac..." stond over de klok van de iPhone.
 * De app reikt tot onder de notch (`viewport-fit=cover`), en de koppen van de
 * schermen schuiven zelf omlaag; deze balk deed dat niet.
 *
 * Hij staat nu VAST bovenaan en neemt de klokzone zelf in. In de paginastroom
 * zou hij elk scherm met `h-screen` langer maken dan het scherm, en de
 * invoerbalk onderaan uit beeld duwen. Wie onder hem staat, schuift op via
 * `--veilig-boven` (index.css): dat is de klokzone, plus de balk zolang hij er
 * is. Twee regels hoogte voor de melding dat de verbinding weg is, die is lang.
 */
export const REGELS = { [LIVE]: 1, [RETURNING]: 1, [LOST]: 2 };

function Balk({ regels, className, testid, children }) {
  useEffect(() => {
    const root = document.documentElement;
    root.setAttribute("data-balk-boven", String(regels));
    return () => root.removeAttribute("data-balk-boven");
  }, [regels]);
  return (
    <div
      className={`fixed inset-x-0 top-0 z-40 flex items-center justify-center gap-2 px-3 text-[11px] border-b pt-[env(safe-area-inset-top)] ${
        regels === 2 ? "h-[calc(2.5rem+env(safe-area-inset-top))]" : "h-[calc(1.5rem+env(safe-area-inset-top))]"
      } ${className}`}
      data-testid={testid}
    >
      {children}
    </div>
  );
}

export default function TunnelBanner() {
  const { t } = useTaal();
  const [state, setState] = useState({ where: "away" });

  useEffect(() => watchTunnel(setState), []);

  if (state.where === LIVE) {
    return (
      <Balk regels={REGELS[LIVE]} className="text-muted-foreground bg-muted" testid="tunnel-live">
        <Laptop className="h-3 w-3" />
        {t("Draait op je Mac, via je eigen verbinding.")}
      </Balk>
    );
  }

  // Being fetched back. Said quietly, and never with an instruction in it:
  // there is nothing for the owner to do, which is the entire point of it.
  if (state.where === RETURNING) {
    return (
      <Balk regels={REGELS[RETURNING]} className="text-muted-foreground bg-muted" testid="tunnel-returning">
        <Loader2 className="h-3 w-3 animate-spin flex-shrink-0" />
        {t("De verbinding met je Mac wordt hersteld.")}
      </Balk>
    );
  }

  if (state.where === LOST) {
    return (
      <Balk regels={REGELS[LOST]} className="text-corona bg-background" testid="tunnel-lost">
        <PlugZap className="h-3 w-3 flex-shrink-0" />
        <span className="line-clamp-2 text-center">
          {state.reason || t("De verbinding met je Mac is weg.")} {t("Scan de code opnieuw om verder te gaan.")}
        </span>
      </Balk>
    );
  }

  return null;
}
